/**
 * @voltxampere/web — Damage state machine (Sprint 36-2)
 *
 * Per-component permanent damage tracking. Sprint 35 emits one
 * `DamageEvent` when a component crosses its severity threshold;
 * Sprint 36 consumes those events and tracks the lifecycle:
 *
 *   healthy → damaged-animating → damaged-permanent
 *               (frame counter)     (animation complete)
 *
 * Permanent state is the consumer surface: Sprint 36-4 circuit-
 * filter excludes permanently damaged components from the engine
 * call (Sprint 23 disabled-component pattern reuse). Restart UI
 * (Sprint 36-5) calls `resetDamage()` to bring everyone back to
 * healthy.
 *
 * LED failure modes (Sprint 36 §"LED damage modes"): on each LED
 * damage event we randomly select 50% open / 50% short. The choice
 * is stored in the entry so reload preserves behavior. `short` mode
 * substitutes a 0.01 Ω resistor stamp; `open` mode excludes the
 * component entirely.
 *
 * Persistence wiring lives in Sprint 36-5 `damage-prefs.ts` (kept
 * separate so this state machine has zero IDB coupling and tests
 * can run without a fake-indexed-db setup). Apps/web boot path
 * threads `restoreDamageSnapshot` after IDB hydration.
 *
 * Engine zero-diff invariant honored — this module imports nothing
 * from `@voltxampere/engine`.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { computed, signal } from "@preact/signals-core";
import type { DamageSnapshotEntry } from "@voltxampere/persistence";
import type { DamageEvent } from "@voltxampere/thermal";

/** Lifecycle status of a damaged component. */
export type DamageStatus = "healthy" | "damaged-animating" | "damaged-permanent";

/** Per-component damage record. */
export interface ComponentDamage {
  readonly componentId: string;
  /** Mirrors `DamageEvent.mode`. */
  readonly mode: DamageEvent["mode"];
  /** Sim time the threshold was crossed (seconds). */
  readonly t: number;
  readonly status: DamageStatus;
  /** Frames since `recordDamageEvent` (resets on `resetDamage`). */
  readonly framesSinceTrigger: number;
  /**
   * LED-specific failure mode. Set on `recordDamageEvent` for LED
   * damage events; absent for other types. `open` = excluded from
   * circuit; `short` = replaced with low-R resistor stamp.
   */
  readonly ledFailureMode?: "open" | "short";
}

/** Frame counts per damage mode (matches Sprint 35 fx primitives). */
const ANIMATION_FRAMES_LED = 45;
const ANIMATION_FRAMES_CAP = 40;
const ANIMATION_FRAMES_RESISTOR = 30;
const ANIMATION_FRAMES_GENERIC = 30;

/** LED short-mode probability (0..1). */
export const LED_SHORT_PROBABILITY = 0.5;

/** Internal RNG hook — tests override for deterministic mode selection. */
let randomFn: () => number = Math.random;

/**
 * Replace the internal random number generator. Tests pass a
 * deterministic stub; production code uses Math.random.
 */
export function __setDamageRandomForTests(fn: () => number): void {
  randomFn = fn;
}

/** Reset RNG to Math.random — tests call in afterEach. */
export function __resetDamageRandomForTests(): void {
  randomFn = Math.random;
}

/** Per-component damage map signal (writable). */
export const damageMap = signal<ReadonlyMap<string, ComponentDamage>>(new Map());

/**
 * Components in any non-healthy state (animating + permanent). Used
 * by the canvas overlay to know who to draw + by the restart UI
 * count badge.
 */
export const damagedComponentIds = computed(() => {
  const ids = new Set<string>();
  for (const [id, d] of damageMap.value) {
    if (d.status !== "healthy") ids.add(id);
  }
  return ids;
});

/** Convenience scalar for restart-button label. */
export const damagedCount = computed(() => damagedComponentIds.value.size);

/**
 * Components in `damaged-permanent` state. Sprint 36-4 circuit-
 * filter consumes this to drop entries from the engine call.
 */
export const damagedPermanentIds = computed(() => {
  const ids = new Set<string>();
  for (const [id, d] of damageMap.value) {
    if (d.status === "damaged-permanent") ids.add(id);
  }
  return ids;
});

/**
 * Record a fresh damage event. Idempotent on the componentId — a
 * second event for the same component is ignored (no-reemit
 * invariant; the thermal damage detector already enforces this,
 * but we double-protect here so the apps/web layer is safe to
 * call from multiple subscribers).
 *
 * For LED events the failure mode is randomly assigned via the
 * internal RNG (50/50 open/short). Other modes leave the field
 * undefined.
 */
export function recordDamageEvent(event: DamageEvent): void {
  if (damageMap.value.has(event.componentId)) {
    const existing = damageMap.value.get(event.componentId);
    if (existing && existing.status !== "healthy") return;
  }
  const newMap = new Map(damageMap.value);
  const ledFailureMode =
    event.mode === "led-pop" ? (randomFn() < LED_SHORT_PROBABILITY ? "short" : "open") : undefined;
  const entry: ComponentDamage = {
    componentId: event.componentId,
    mode: event.mode,
    t: event.t,
    status: "damaged-animating",
    framesSinceTrigger: 0,
    ...(ledFailureMode !== undefined ? { ledFailureMode } : {}),
  };
  newMap.set(event.componentId, entry);
  damageMap.value = newMap;
}

/**
 * Advance every animating component's frame counter by 1; transition
 * to `damaged-permanent` when the per-mode animation length is
 * reached. No-op when the map has no animating entries.
 *
 * Caller wires this into the canvas RAF loop alongside the
 * explosion-renderer step.
 */
export function tickDamageStates(): void {
  let changed = false;
  const newMap = new Map(damageMap.value);
  for (const [id, d] of damageMap.value) {
    if (d.status !== "damaged-animating") continue;
    const nextFrames = d.framesSinceTrigger + 1;
    const animLen = framesForMode(d.mode);
    if (nextFrames >= animLen) {
      newMap.set(id, { ...d, status: "damaged-permanent", framesSinceTrigger: nextFrames });
    } else {
      newMap.set(id, { ...d, framesSinceTrigger: nextFrames });
    }
    changed = true;
  }
  if (changed) damageMap.value = newMap;
}

/** Drop the entire damage map — restart-circuit + circuit-load path. */
export function resetDamage(): void {
  damageMap.value = new Map();
}

/** Convenience: is this component currently damaged (any state)? */
export function isDamaged(componentId: string): boolean {
  return damagedComponentIds.value.has(componentId);
}

/**
 * Convenience: is this component permanently damaged? Sprint 36-4
 * circuit-filter checks this to decide engine inclusion.
 */
export function isPermanentlyDamaged(componentId: string): boolean {
  return damagedPermanentIds.value.has(componentId);
}

/**
 * Snapshot current damage state for IDB persistence. Only the
 * permanently damaged entries make it into the snapshot — animating
 * entries are mid-transition and would land in an inconsistent
 * state on restore.
 */
export function snapshotDamageState(): readonly DamageSnapshotEntry[] {
  const out: DamageSnapshotEntry[] = [];
  for (const [, d] of damageMap.value) {
    if (d.status !== "damaged-permanent") continue;
    out.push({
      id: d.componentId,
      mode: d.mode,
      t: d.t,
      ...(d.ledFailureMode !== undefined ? { ledFailureMode: d.ledFailureMode } : {}),
    });
  }
  return out;
}

/**
 * Restore a snapshot as permanent damage. Animation never replays —
 * the user's reload should land them in the post-explosion state.
 * Snapshot entries with unknown / malformed `mode` are silently
 * skipped (defensive — handles forward-compat schema drift).
 */
export function restoreDamageSnapshot(snapshot: readonly DamageSnapshotEntry[]): void {
  const newMap = new Map<string, ComponentDamage>();
  for (const entry of snapshot) {
    const mode = parseDamageMode(entry.mode);
    if (mode === null) continue;
    const ledFailureMode =
      entry.ledFailureMode === "open" || entry.ledFailureMode === "short"
        ? entry.ledFailureMode
        : undefined;
    newMap.set(entry.id, {
      componentId: entry.id,
      mode,
      t: entry.t,
      status: "damaged-permanent",
      framesSinceTrigger: framesForMode(mode),
      ...(ledFailureMode !== undefined ? { ledFailureMode } : {}),
    });
  }
  damageMap.value = newMap;
}

function framesForMode(mode: DamageEvent["mode"]): number {
  if (mode === "led-pop") return ANIMATION_FRAMES_LED;
  if (mode === "capacitor-burst") return ANIMATION_FRAMES_CAP;
  if (mode === "resistor-burn") return ANIMATION_FRAMES_RESISTOR;
  return ANIMATION_FRAMES_GENERIC;
}

function parseDamageMode(s: string): DamageEvent["mode"] | null {
  if (
    s === "led-pop" ||
    s === "capacitor-burst" ||
    s === "resistor-burn" ||
    s === "generic-overheat"
  ) {
    return s;
  }
  return null;
}
